import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * Hook to load all dashboard data in a single optimized RPC call
 * Replaces ~125+ individual API calls with 1-2 calls
 */
export function useDashboardData() {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);

  /**
   * Transform raw RPC data into the format expected by dashboard components
   */
  const transformProjectData = useCallback((project, settings) => {
    // Apply saved colors from user settings (user override takes precedence)
    const savedColor = settings?.project_colors?.[project.id];
    // Apply saved pinned status from user settings
    const isPinned = settings?.pinned_projects?.includes(project.id) || false;

    // Count enabled tools
    let toolCount = 0;
    if (project.tools) {
      try {
        const toolsObj = typeof project.tools === 'string'
          ? JSON.parse(project.tools)
          : project.tools;
        toolCount = Object.values(toolsObj).filter(enabled => enabled === true).length;
      } catch (e) {
        console.error('Error parsing tools for project:', project.id, e);
      }
    }

    return {
      id: project.id,
      name: project.name,
      type: project.type,
      description: project.description,
      location: project.location,
      location_data: project.location_data,
      industry: project.industry,
      avatar: project.avatar,
      color: savedColor || project.color, // User override if exists, otherwise project-level color
      isPinned: isPinned,
      teamMembers: project.team_members || [], // Already formatted from RPC
      visibility_type: project.visibility_type,
      toolCount: toolCount,
      createdAt: new Date(project.created_date),
      is_archived: project.is_archived,
      // NEW: Compliance data from server (eliminates N+1 queries)
      complianceSummary: project.compliance_summary || {
        percentage: 0,
        valid: 0,
        expiring: 0,
        expired: 0,
        missing: 0,
        total_required: 0
      }
    };
  }, []);

  /**
   * Load dashboard data using the optimized RPC
   * @param {Object} params - Load parameters
   * @param {string} params.companyId - Company ID
   * @param {string} params.userId - User profile ID
   * @param {boolean} params.includeArchived - Whether to include archived projects
   * @param {number} params.limit - Number of projects to load
   * @param {number} params.offset - Offset for pagination
   * @param {string} params.search - Optional search term
   */
  const loadDashboardData = useCallback(async ({
    companyId,
    userId,
    includeArchived = true,
    limit = 50,
    offset = 0,
    search = null
  }) => {
    if (loadingRef.current) {
      console.log('[useDashboardData] Already loading, skipping...');
      return null;
    }

    if (!companyId || !userId) {
      console.log('[useDashboardData] Missing companyId or userId');
      return null;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[useDashboardData] Loading dashboard data with single RPC call...');
      console.time('[useDashboardData] RPC call duration');

      // Single RPC call replaces ~125+ individual queries!
      const { data, error: rpcError } = await supabase.rpc('get_dashboard_data_v2', {
        p_company_id: companyId,
        p_user_id: userId,
        p_include_archived: includeArchived,
        p_limit: limit,
        p_offset: offset,
        p_search: search || null
      });

      console.timeEnd('[useDashboardData] RPC call duration');

      if (rpcError) {
        console.error('[useDashboardData] RPC error:', rpcError);
        setError(rpcError.message);
        return null;
      }

      console.log('[useDashboardData] Raw RPC response:', {
        projectCount: data?.projects?.length || 0,
        archivedCount: data?.archived_projects?.length || 0,
        hasSettings: !!data?.settings,
        totalCount: data?.total_count
      });

      const settings = data?.settings || {};

      // Transform active projects
      const transformedProjects = (data?.projects || []).map(project =>
        transformProjectData(project, settings)
      );

      // Transform archived projects
      const transformedArchived = (data?.archived_projects || []).map(project =>
        transformProjectData(project, settings)
      );

      // Reconstruct stacks from settings
      const savedStacks = settings?.stacks || [];
      const allProjects = [...transformedProjects, ...transformedArchived];

      // Filter out projects that are in stacks from main lists
      const stackedProjectIds = savedStacks.flatMap(stack => stack.projectIds || []);
      const availableProjects = transformedProjects.filter(
        project => !stackedProjectIds.includes(project.id)
      );
      const availableArchived = transformedArchived.filter(
        project => !stackedProjectIds.includes(project.id)
      );

      // Reconstruct stacks with actual project data
      const reconstructedStacks = savedStacks.map(stack => {
        const stackProjects = (stack.projectIds || [])
          .map(projectId => allProjects.find(p => p.id === projectId))
          .filter(Boolean);

        // Calculate aggregated compliance for stack
        const stackCompliance = calculateStackCompliance(stackProjects);

        return {
          ...stack,
          projects: stackProjects,
          isPinned: stack.isPinned || false,
          color: stack.color,
          complianceSummary: stackCompliance
        };
      }).filter(stack => stack.projects.length > 0);

      // Separate active and archived stacks
      const activeStacks = reconstructedStacks.filter(
        stack => !stack.projects.every(p => p.is_archived)
      );
      const archivedStacks = reconstructedStacks.filter(
        stack => stack.projects.every(p => p.is_archived)
      );

      // Parse order arrays
      const projectOrder = parseOrderArray(settings?.project_order);
      const stackOrder = parseOrderArray(settings?.stack_order);
      const combinedOrder = parseOrderArray(settings?.combined_order);

      const result = {
        projects: availableProjects,
        archivedProjects: availableArchived,
        stacks: activeStacks,
        archivedStacks: archivedStacks,
        settings: settings,
        projectOrder: projectOrder.length > 0 ? projectOrder : availableProjects.map(p => p.id),
        stackOrder: stackOrder.length > 0 ? stackOrder : activeStacks.map(s => s.id),
        combinedOrder: combinedOrder, // Combined order for mixed project/stack display
        totalCount: data?.total_count || 0,
        archivedCount: data?.archived_count || 0,
        hasMore: (data?.projects?.length || 0) >= limit
      };

      console.log('[useDashboardData] Transformed result:', {
        projectCount: result.projects.length,
        archivedCount: result.archivedProjects.length,
        stackCount: result.stacks.length,
        hasMore: result.hasMore
      });

      return result;
    } catch (err) {
      console.error('[useDashboardData] Error:', err);
      setError(err.message);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [transformProjectData]);

  /**
   * Load more dashboard data for pagination (uses same optimized RPC)
   * @param {Object} params - Load parameters
   * @param {string} params.companyId - Company ID
   * @param {string} params.userId - User profile ID
   * @param {boolean} params.includeArchived - Whether to include archived projects
   * @param {number} params.limit - Number of projects to load
   * @param {number} params.offset - Offset for pagination
   * @param {string} params.search - Optional search term
   * @param {Object} params.existingSettings - Existing settings to avoid re-fetching
   */
  const loadMoreDashboardData = useCallback(async ({
    companyId,
    userId,
    includeArchived = true,
    limit = 24,
    offset = 0,
    search = null,
    existingSettings = null
  }) => {
    if (loadingMoreRef.current) {
      return null;
    }

    if (!companyId || !userId) {
      return null;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      // Use same optimized RPC for pagination
      const { data, error: rpcError } = await supabase.rpc('get_dashboard_data_v2', {
        p_company_id: companyId,
        p_user_id: userId,
        p_include_archived: includeArchived,
        p_limit: limit,
        p_offset: offset,
        p_search: search || null
      });

      if (rpcError) {
        console.error('[useDashboardData] LoadMore RPC error:', rpcError);
        setError(rpcError.message);
        return null;
      }

      // Use existing settings if provided, otherwise use from response
      const settings = existingSettings || data?.settings || {};

      // Transform only the new projects (no stacks reconstruction needed for pagination)
      const transformedProjects = (data?.projects || []).map(project =>
        transformProjectData(project, settings)
      );

      // Filter out projects that are in stacks
      const savedStacks = settings?.stacks || [];
      const stackedProjectIds = savedStacks.flatMap(stack => stack.projectIds || []);
      const availableProjects = transformedProjects.filter(
        project => !stackedProjectIds.includes(project.id)
      );

      return {
        projects: availableProjects,
        hasMore: (data?.projects?.length || 0) >= limit,
        totalCount: data?.total_count || 0
      };
    } catch (err) {
      console.error('[useDashboardData] LoadMore error:', err);
      setError(err.message);
      return null;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [transformProjectData]);

  return {
    loadDashboardData,
    loadMoreDashboardData,
    loading,
    loadingMore,
    error
  };
}

/**
 * Calculate aggregated compliance for a stack of projects
 * Exported for use when dynamically updating stacks (e.g., drag-and-drop)
 */
export function calculateStackCompliance(projects) {
  if (!projects || projects.length === 0) {
    return { percentage: 0, valid: 0, expiring: 0, expired: 0, missing: 0, total_required: 0 };
  }

  const totals = projects.reduce((acc, project) => {
    const cs = project.complianceSummary || {};
    return {
      valid: acc.valid + (cs.valid || 0),
      expiring: acc.expiring + (cs.expiring || 0),
      expired: acc.expired + (cs.expired || 0),
      missing: acc.missing + (cs.missing || 0),
      total_required: acc.total_required + (cs.total_required || 0),
      percentageSum: acc.percentageSum + (cs.percentage || 0)
    };
  }, { valid: 0, expiring: 0, expired: 0, missing: 0, total_required: 0, percentageSum: 0 });

  // Average compliance percentage across projects
  const percentage = projects.length > 0
    ? Math.round(totals.percentageSum / projects.length)
    : 0;

  return {
    percentage,
    valid: totals.valid,
    expiring: totals.expiring,
    expired: totals.expired,
    missing: totals.missing,
    total_required: totals.total_required
  };
}

/**
 * Parse order array from settings (handles both formats)
 */
function parseOrderArray(orderData) {
  if (!orderData || !Array.isArray(orderData) || orderData.length === 0) {
    return [];
  }

  // Handle both formats: [{id, order}] or just [id, id, id]
  if (typeof orderData[0] === 'object' && orderData[0]?.id !== undefined) {
    // New format: [{id, order}] - sort by order and extract IDs
    return [...orderData]
      .sort((a, b) => a.order - b.order)
      .map(item => item.id);
  } else {
    // Old format: [id, id, id]
    return orderData;
  }
}
